<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';
import type { StringOptions } from '~kui/types/selectOption';

import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

import type { AboutYouAnswers, AboutYouField } from '../types';

import { ALL_ABOUT_YOU_FIELDS } from '../types';

/**
 * `latestBirthMonthYear` (YYYY-MM) is the latest birth month the picker offers. Sign-in passes the
 * server's account floor so a returning student cannot enter a month the server would refuse;
 * sign-up leaves it unset so every month up to today is offered and the too-young screen can turn
 * a new student away.
 */
interface Props {
  countryStates: StringOptions[];
  fields?: AboutYouField[];
  initialFirstName?: string;
  initialLastName?: string;
  result?: ResponseResult | null;
  loading?: boolean;
  latestBirthMonthYear?: string;
}

const props = withDefaults(defineProps<Props>(), {
  fields: () => [...ALL_ABOUT_YOU_FIELDS],
  initialFirstName: '',
  initialLastName: '',
  result: null,
  loading: false,
  latestBirthMonthYear: undefined,
});

const emit = defineEmits<{
  submit: [payload: AboutYouAnswers];
}>();

const { t, locale } = useI18n();

const YEAR_OPTION_COUNT = 100;
// The latest birth year on offer and, in that year only, the latest month: a later month is either
// in the future or under the account age.
const today = new Date();
const latestYear = props.latestBirthMonthYear ? Number(props.latestBirthMonthYear.slice(0, 4)) : today.getFullYear();
const latestMonth = props.latestBirthMonthYear ? Number(props.latestBirthMonthYear.slice(5, 7)) : today.getMonth() + 1;
const allMonthOptions: StringOptions[] = Array.from({ length: 12 }, (_, i) => ({
  // Three letters so month and year fit side by side; some locales abbreviate September as "Sept".
  label: new Date(2000, i, 1).toLocaleString(locale.value, { month: 'short' }).slice(0, 3),
  value: String(i + 1).padStart(2, '0'),
}));
const allYearOptions: StringOptions[] = Array.from({ length: YEAR_OPTION_COUNT }, (_, i) => ({
  label: String(latestYear - i),
  value: String(latestYear - i),
}));

const selectedMonth = ref('');
const selectedYear = ref('');
const monthOptions = computed(() =>
  selectedYear.value === String(latestYear) ? allMonthOptions.slice(0, latestMonth) : allMonthOptions,
);
const yearOptions = computed(() =>
  Number(selectedMonth.value) > latestMonth ? allYearOptions.slice(1) : allYearOptions,
);
// Spread into a select's field options: KUICoreForm v-binds fieldConfig.props onto the input, so Vue merges this listener with the form's own update:model-value.
const trackMonth = {
  'onUpdate:modelValue': (value?: string | number | null) => {
    selectedMonth.value = String(value ?? '');
  },
};
const trackYear = {
  'onUpdate:modelValue': (value?: string | number | null) => {
    selectedYear.value = String(value ?? '');
  },
};

const show = (field: AboutYouField) => props.fields.includes(field);

const formConfig = computed(() => defineFormConfig(
  {
    ...(show('firstName')
      ? {
          firstName: defineField({
            wrapperClass: 'col-span-2',
            type: 'text',
            options: {
              label: t('unified.onboarding.aboutYouFields.firstName'),
              dataTestId: 'onboarding-first-name',
              autocomplete: 'given-name',
              required: true,
            },
            initialValue: props.initialFirstName,
            schema: z.string().trim().min(1, t('general.formFields.firstName.required')).max(255),
          }),
        }
      : {}),
    ...(show('lastName')
      ? {
          lastName: defineField({
            wrapperClass: 'col-span-2',
            type: 'text',
            options: {
              label: t('unified.onboarding.aboutYouFields.lastName'),
              dataTestId: 'onboarding-last-name',
              autocomplete: 'family-name',
              required: true,
            },
            initialValue: props.initialLastName,
            schema: z.string().trim().min(1, t('general.formFields.lastName.required')).max(255),
          }),
        }
      : {}),
    ...(show('birthMonthYear')
      ? {
          birthMonth: defineField({
            type: 'select',
            options: {
              as: 'select',
              version: 'v2',
              label: t('unified.onboarding.aboutYouFields.birthMonth'),
              options: monthOptions.value,
              dataTestId: 'onboarding-birth-month',
              required: true,
              ...trackMonth,
            },
            initialValue: '',
            schema: z.string().min(1, t('general.formFields.birthDate.required')),
          }),
          birthYear: defineField({
            type: 'select',
            options: {
              as: 'select',
              version: 'v2',
              label: t('unified.onboarding.aboutYouFields.birthYear'),
              options: yearOptions.value,
              dataTestId: 'onboarding-birth-year',
              required: true,
              ...trackYear,
            },
            initialValue: '',
            schema: z.string().min(1, t('general.formFields.birthDate.required')),
          }),
        }
      : {}),
    ...(show('stateId')
      ? {
          stateId: defineField({
            wrapperClass: 'col-span-2',
            type: 'select',
            options: {
              as: 'select',
              version: 'v2',
              label: t('unified.onboarding.aboutYouFields.state'),
              options: props.countryStates,
              dataTestId: 'onboarding-state',
              required: true,
            },
            initialValue: '',
            schema: z.string().min(1, t('general.formFields.state.required')),
          }),
        }
      : {}),
  },
  t('general.continue'),
));

const handleSubmit = (formValues: Record<string, unknown>) => {
  const values = formValues as Record<string, string>;
  emit('submit', {
    ...(show('firstName') ? { firstName: values.firstName } : {}),
    ...(show('lastName') ? { lastName: values.lastName } : {}),
    ...(show('birthMonthYear') ? { birthMonthYear: `${values.birthYear}-${values.birthMonth}` } : {}),
    ...(show('stateId') ? { stateId: values.stateId } : {}),
  });
};
</script>

<template>
  <div data-testid="onboarding-about-you-step">
    <KUICoreForm
      fields-wrapper-class="my-6 md:my-4 grid grid-cols-2 gap-x-3 gap-y-4 md:gap-y-2"
      :form-config="formConfig"
      :form-result="result"
      :loading="loading"
      @submit="handleSubmit"
    />
  </div>
</template>
